import L, { type LatLng, type Map as LeafletMap } from 'leaflet';
import { TacticalSymbol, type SerializedSymbol } from './TacticalSymbol';
import { SIDC_AXIS_OF_ADVANCE } from './sidc';
import { type MilSym } from './renderer';
import sharedAttackLine from './shared/attackLine';

/**
 * Ground distances, in metres, that fully define an Axis-of-Advance arrow
 * alongside its centreline.
 */
export interface AttackLineParams {
    width_m: number;
    head_length_m: number;
}

/**
 * Axis of Advance – Main Attack arrow (draw rule "AXIS").
 *
 * The graphic is defined by its centreline plus two ground distances. The
 * control-point derivation itself lives in {@link ./shared/attackLine} — a
 * dependency-free module copied verbatim from the umbrella repo into both this
 * client and the Android WebView, so a shared arrow renders identically on
 * every device instead of drifting between two ports of the same math.
 *
 * Width and head length are *authored* in screen pixels (a fresh arrow is a
 * sensible on-screen size at any zoom) and converted to metres once, at
 * finalize. That conversion is the difference between a shared graphic and a
 * suggestion: 60 px is ~1470 m of ground at zoom 12 but ~92 m at zoom 16, so an
 * unconverted arrow means something different on every screen.
 */
export class AttackLine extends TacticalSymbol {
    readonly minPoints = 2;

    /**
     * `sidc` defaults to the friendly, present variant so a bare `new
     * AttackLine(map, milSym)` still means what it always did; the registry
     * passes the stored code through instead, affiliation digits and all.
     */
    constructor(map: LeafletMap, milSym: MilSym, sidc: string = SIDC_AXIS_OF_ADVANCE) {
        super(map, milSym, sidc);
    }

    /**
     * Stored ground distances. Null until the graphic is finalised or restored
     * from persistence, in which case the pixel defaults are used for preview.
     */
    private params: AttackLineParams | null = null;

    /** Restore params from a persisted graphic. */
    setParams(params: AttackLineParams | null): void {
        this.params = params;
    }

    protected controlPoints(points: LatLng[]): LatLng[] {
        const asPairs = points.map((p) => [p.lng, p.lat]);
        const params = this.params ?? this.paramsFromPixelDefaults(points);
        const derived = sharedAttackLine.controlPoints(asPairs, params);
        if (!derived) return points;
        return derived.map(([lng, lat]) => L.latLng(lat, lng));
    }

    serialize(): SerializedSymbol {
        const points = this.points.map((p) => [p.lng, p.lat] as [number, number]);
        const params = this.params ?? this.paramsFromPixelDefaults(this.points);
        return {
            sidc: this.sidc,
            points,
            width_m: params.width_m,
            head_length_m: params.head_length_m,
        };
    }

    /**
     * Convert the pixel authoring defaults into ground metres at the current
     * zoom, measured at the arrow's tip.
     *
     * Called while drawing (so the preview tracks the pixel feel) and once at
     * finalize (so what gets stored is zoom-independent).
     */
    private paramsFromPixelDefaults(points: LatLng[]): AttackLineParams {
        const tip = points[points.length - 1];
        const lat = tip ? tip.lat : this.map.getCenter().lat;
        const mPerPx = sharedAttackLine.metersPerPixel(lat, this.map.getZoom());
        return {
            width_m: sharedAttackLine.DEFAULT_WIDTH_PX * mPerPx,
            head_length_m: sharedAttackLine.DEFAULT_HEAD_LENGTH_PX * mPerPx,
        };
    }
}
